import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";

import Checkbox from "@components/inputs/Checkbox";
import DropDownMenu from "@components/inputs/DropDownMenu";
import Label from "@components/inputs/text/Label";
import SubmitBtn from "@components/buttons/FormButtons/SubmitBtn";
import { NavBar } from "@components/navigation/NavBar/NavBar";
import { InfoBox, WarningBox } from "@components/cards/messageBox";
import { IPricingRecord } from "@services/QuoteBuilder/QuoteBuilderService";
import { PricingRecordAction } from "./pricingRecordReducer";
import PlanOptionComponent from "./PlanOptionComponent";
import {
  OptionalChargebeeComponents,
  optOutDeclarationLabel,
  optOutEvidenceOptions,
} from "@utils/quotePlanOptions";
import { Country } from "@utils/constants/localisation";
import { countrySpecificCurrency } from "@utils/misc/functions";


type Props = {
  pricingRecordState: IPricingRecord | null;
  pricingRecordDispatch: React.Dispatch<PricingRecordAction>;
  navigateLandingPageTarget: string;
  navigateNextTarget: string;
  foQuoting21Enabled: boolean;
};


export default function PlanOptionsView({
  pricingRecordState,
  pricingRecordDispatch,
  navigateLandingPageTarget,
  navigateNextTarget,
  foQuoting21Enabled,
}: Props) {
  const navigate = useNavigate();

  const country = pricingRecordState?.country as Country;
  const currency = countrySpecificCurrency(country);

  const msalContext = useMsal();
  const userDetails = msalContext.instance.getActiveAccount();

  const lossAndDamageWaiverReductionComponentList: string[] = useMemo(
    () => {
      return pricingRecordState?.component_lists?.loss_and_damage_waiver_reduction
        ? pricingRecordState?.component_lists?.loss_and_damage_waiver_reduction
        : []
    },
    [pricingRecordState?.component_lists?.loss_and_damage_waiver_reduction]
  );

  // Base Part C only — deliberately excludes Cover Plus and the waiver reduction tiers, so
  // opting out of those alone never counts as opting out of Part C
  const baseLossAndDamageLineItem = useMemo(
    () => pricingRecordState?.pricing_record_line_items.find(
      (lineItem) => lineItem.chargebee_component === OptionalChargebeeComponents.LOSS_AND_DAMAGE_PAYMENT
        || lineItem.chargebee_component === OptionalChargebeeComponents.LOSS_AND_DAMAGE_COVER_VEHICLE
    ),
    [pricingRecordState?.pricing_record_line_items]
  );

  const partCIncluded = !!baseLossAndDamageLineItem?.included;
  const partCOptedOut = !!baseLossAndDamageLineItem && !baseLossAndDamageLineItem.included;

  useEffect(() => {
    if (pricingRecordState === null) {
      navigate(navigateLandingPageTarget);
    }

    // Reset Payout Waiver Opt-out Evidence if Payout Waiver Fee is "Included"
    const payoutWaiverFee =
      pricingRecordState?.pricing_record_line_items.filter(
        (lineItem) => lineItem.chargebee_component === OptionalChargebeeComponents.PAYOUT_PROTECTION_FEE
      );
    if (
      payoutWaiverFee &&
      payoutWaiverFee.length > 0 &&
      payoutWaiverFee[0].included
    ) {
      if (pricingRecordState?.payout_waiver_opt_out_evidence !== null) {
        pricingRecordDispatch({
          type: "UPDATE_FIELD",
          payload: {
            field: "payout_waiver_opt_out_evidence",
            value: null,
          },
        });
      }
      if (
        pricingRecordState?.payout_waiver_opt_out_evidence_checked_by !== null
      ) {
        pricingRecordDispatch({
          type: "UPDATE_FIELD",
          payload: {
            field: "payout_waiver_opt_out_evidence_checked_by",
            value: null,
          },
        });
      }
    }

    // Part C guards Payout Protection. Once Part C is opted out the fee cannot be carried, and
    // any Payout Protection evidence already captured describes an opt-out that no longer exists.
    // Guarded on truthiness rather than `!== null` because these fields are undefined on records
    // created before the feature shipped, and the effect depends on the whole pricing record.
    if (partCOptedOut) {
      const payoutProtectionLineItem =
        pricingRecordState?.pricing_record_line_items.find(
          (lineItem) => lineItem.chargebee_component === OptionalChargebeeComponents.PAYOUT_PROTECTION_FEE
        );

      if (payoutProtectionLineItem?.included) {
        pricingRecordDispatch({
          type: "EXCLUDE_PRICING_RECORD_DETAIL",
          payload: payoutProtectionLineItem.pricing_template_line_item_id,
        });
      }

      if (pricingRecordState?.payout_waiver_opt_out_evidence) {
        pricingRecordDispatch({
          type: "UPDATE_FIELD",
          payload: { field: "payout_waiver_opt_out_evidence", value: null },
        });
      }
      if (pricingRecordState?.payout_waiver_opt_out_evidence_checked_by) {
        pricingRecordDispatch({
          type: "UPDATE_FIELD",
          payload: {
            field: "payout_waiver_opt_out_evidence_checked_by",
            value: null,
          },
        });
      }
    }

    // Part C back in: its opt-out evidence no longer applies. Payout Protection deliberately
    // stays excluded — the agent must choose it again, which is then a genuine Payout Protection
    // opt-out carrying its own evidence.
    if (partCIncluded) {
      if (pricingRecordState?.loss_and_damage_opt_out_evidence) {
        pricingRecordDispatch({
          type: "UPDATE_FIELD",
          payload: { field: "loss_and_damage_opt_out_evidence", value: null },
        });
      }
      if (pricingRecordState?.loss_and_damage_opt_out_evidence_checked_by) {
        pricingRecordDispatch({
          type: "UPDATE_FIELD",
          payload: {
            field: "loss_and_damage_opt_out_evidence_checked_by",
            value: null,
          },
        });
      }
    }

    // GB only: keep Third Party Property (C1) and Vehicle L&D (C2) in sync.
    //
    // In the GB pricing templates (19/20), Loss & Damage cover is split into
    // two separate line items:
    //   C1 = "Loss and Damage Payment - Third Party Property and Theft" (TPP)
    //   C2 = "Loss and Damage Payment - Vehicle"
    //
    // These are presented to the CSR as a single toggle in the quote builder.
    // When the CSR includes or excludes Vehicle L&D (C2), TPP (C1) must follow
    // automatically so they are always in the same state.
    //
    // The backend relies on this coupling — e.g. is_loss_and_damage_cover()
    // checks C1 to determine if L&D cover is opted in, so C1 must always
    // reflect the same included state as C2.
    if (country === Country.GB) {
      const tppItem = pricingRecordState?.pricing_record_line_items.find(
        (lineItem) => lineItem.chargebee_component === OptionalChargebeeComponents.LOSS_AND_DAMAGE_COVER_PAYMENT_TPP
          || lineItem.chargebee_component === OptionalChargebeeComponents.LOSS_AND_DAMAGE_COVER_TPP_AND_PI
      );

      if (baseLossAndDamageLineItem && tppItem && baseLossAndDamageLineItem.included !== tppItem.included) {
        pricingRecordDispatch({
          type: baseLossAndDamageLineItem.included ? "INCLUDE_PRICING_RECORD_DETAIL" : "EXCLUDE_PRICING_RECORD_DETAIL",
          payload: tppItem.pricing_template_line_item_id,
        });
      }
    }

    // Reset Loss & Damage Waiver Reduction Fee if Loss & Damage Payment is "Not included"
    if (partCOptedOut) {
      // Exclude excess reduction
      pricingRecordState?.pricing_record_line_items.forEach((lineItem) => {
        if (
          lossAndDamageWaiverReductionComponentList.includes(lineItem.chargebee_component) &&
          lineItem.included
        ) {
          pricingRecordDispatch({
            type: "EXCLUDE_PRICING_RECORD_DETAIL",
            payload: lineItem.pricing_template_line_item_id,
          });
        }

        if (
          lineItem.chargebee_component === OptionalChargebeeComponents.LOSS_AND_DAMAGE_COVER_PLUS &&
          lineItem.included
        ) {
          pricingRecordDispatch({
            type: "EXCLUDE_PRICING_RECORD_DETAIL",
            payload: lineItem.pricing_template_line_item_id,
          });
        }
      });
    }

  }, [
    pricingRecordState,
    navigateLandingPageTarget,
    navigate,
    pricingRecordDispatch,
    lossAndDamageWaiverReductionComponentList,
    baseLossAndDamageLineItem,
    partCOptedOut,
    partCIncluded,
  ]);

  const handleOptOutEvidenceChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    pricingRecordDispatch({
      type: "UPDATE_FIELD",
      payload: {
        field: e.target.name,
        value: e.currentTarget.value,
      },
    });
  };

  const handleCheckboxChange = (field: string, checkedBy?: string | null) => {
    const localIsChecked = !(checkedBy ? true : false);
    let loginUser: string | undefined = "";

    // Set the user name when checkbox is checked
    if (localIsChecked) {
      loginUser = userDetails?.name;
    }
    pricingRecordDispatch({
      type: "UPDATE_FIELD",
      payload: {
        field,
        value: loginUser,
      },
    });
  };

  /**
   * On submit handler
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(navigateNextTarget);
  };

  return (
    <>
      <NavBar pageTitle="Plan Options" />
      <form style={{ margin: "2.5vh 2.5vw" }} onSubmit={handleSubmit}>
        <Label text="Plan options" styleCfg={{ bold: true }} />
        <div style={{ margin: "1vh 0vw" }}>
          <p>
            Please indicate if the quote will include the{" "}
            <strong>Loss & damage payment</strong>:
          </p>
        </div>
        <InfoBox
          message={
            <>
              The Loss & Damage Payment covers the customer in the case of
              vehicle damage or loss (subject to payment of the Loss and Damage
              waiver).
            </>
          }
        />
        <PlanOptionComponent
          name="PlanOption-LossAndDamage"
          pricingRecordState={pricingRecordState}
          pricingRecordDispatch={pricingRecordDispatch}
          optionList={[
            OptionalChargebeeComponents.LOSS_AND_DAMAGE_PAYMENT,
            OptionalChargebeeComponents.LOSS_AND_DAMAGE_COVER_VEHICLE,
            OptionalChargebeeComponents.SPLEND_MANAGEMENT_FEE,
          ]}
        />

        {pricingRecordState?.pricing_record_line_items.filter(
          (item) => item.chargebee_component === OptionalChargebeeComponents.SPLEND_MANAGEMENT_FEE
        )[0].included && (
          <>
            <WarningBox
              testId={"WarningBox-LossAndDamageWaiver-OptOut"}
              message={
                <>
                  Opt-outs of the Loss & Damage cover are subject to approval.
                  If approved, the customer will be subject to a Splend
                  Management fee.
                </>
              }
            />
          </>
        )}

        {partCOptedOut && (
          <>
            <Label text="Opt-out evidence" styleCfg={{ bold: true }} />
            <div style={{ margin: "1vh 0vw" }}></div>
            <Label text={"Evidence Type"} requiredFieldInd={true} />
            <div style={{ margin: "1vh 0 2.5vh 0" }}>
              <DropDownMenu
                menuName="loss_and_damage_opt_out_evidence"
                required={true}
                choices={optOutEvidenceOptions}
                onSelect={handleOptOutEvidenceChange}
                testId="LossAndDamageOptOutEvidence-Dropdown"
                defaultVal={
                  pricingRecordState?.loss_and_damage_opt_out_evidence
                    ? {
                        value:
                          pricingRecordState?.loss_and_damage_opt_out_evidence,
                      }
                    : undefined
                }
              />
            </div>
            <Checkbox
              label={optOutDeclarationLabel(
                userDetails?.name,
                "the Loss & Damage cover"
              )}
              onChange={() =>
                handleCheckboxChange(
                  "loss_and_damage_opt_out_evidence_checked_by",
                  pricingRecordState?.loss_and_damage_opt_out_evidence_checked_by
                )
              }
              id="confirmedLossAndDamage"
              name="loss_and_damage_opt_out_evidence_checked_by"
              required={true}
              checked={
                pricingRecordState?.loss_and_damage_opt_out_evidence_checked_by
                  ? true
                  : false
              }
              testId="LossAndDamageOptOutEvidence-Checkbox"
            />
          </>
        )}

        {partCIncluded && (
          <>
            <Divider />
            <div style={{ margin: "1vh 0vw" }}>
              <p>
                Please indicate if the quote will include{" "}
                <strong>Loss & Damage Cover Plus</strong>:
              </p>
            </div>
            <InfoBox
              message={
                <>
                  The Loss & Damage Payment covers the customer in case of 
                  a vehicle damage or loss (subject to payment of the Loss 
                  and Damage Waiver).
                </>
              }
            />
            <PlanOptionComponent
              name="PlanOption-LossAndDamageCoverPlus"
              pricingRecordState={pricingRecordState}
              pricingRecordDispatch={pricingRecordDispatch}
              optionList={[OptionalChargebeeComponents.LOSS_AND_DAMAGE_COVER_PLUS]}
              noneOption={{ label: "Not included", value: "", enabled: true }}
            />
          </>
        )}


        {partCIncluded && (
          <>
            <Divider />
            <div style={{ margin: "1vh 0vw" }}>
              <p>
                Please select a{" "}
                <strong>Loss & damage Waiver Reduction Fee</strong>:
              </p>
            </div>
            <Label text={`Loss and Damage Waiver amount (${currency})`} requiredFieldInd={true} />
            <InfoBox
              testId={"InfoBox-LossAndDamageWaiver-OptIn"}
              message={
                <>
                  Default Loss and Damage Waiver included: {currency}{country === Country.GB ? "1000" : "1500"}.00. Choosing to
                  pay the Loss and Damage Reduction fee will reduce your Loss
                  and Damage Waiver amount.
                </>
              }
            />
            <PlanOptionComponent
              name="PlanOption-ExcessReduction"
              pricingRecordState={pricingRecordState}
              pricingRecordDispatch={pricingRecordDispatch}
              optionList={lossAndDamageWaiverReductionComponentList}
              noneOption={{ label: country == Country.GB ? `${currency}1000` : `${currency}1500`, value: "", enabled: true }}
            />
          </>
        )}

        <Divider />
        <div style={{ margin: "1vh 0vw" }}>
          <p>
            Please indicate if the quote will include the{" "}
            <strong>payout protection fee</strong>:
          </p>
        </div>
        {partCOptedOut && (
          <InfoBox
            testId={"InfoBox-PayoutProtectionFee-LockedByPartC"}
            message={
              <>
                {"Locked to Not included - Payout Protection can't be added once Part C is opted out."}
              </>
            }
          />
        )}
        {!partCOptedOut &&
        pricingRecordState?.pricing_record_line_items.filter(
          (item) => item.chargebee_component === OptionalChargebeeComponents.PAYOUT_PROTECTION_FEE
        )[0].included === true && (
          <InfoBox
            testId={"InfoBox-PayoutProtectionFee-OptIn"}
            message={
              <>
                The Payout Protection Fee applies to have the termination amount
                waived in the event of early termination of the Agreement, so
                long as termination does not occur before 52 weeks.
              </>
            }
          />
        )}
        {!partCOptedOut &&
        pricingRecordState?.pricing_record_line_items.filter(
          (item) => item.chargebee_component === OptionalChargebeeComponents.PAYOUT_PROTECTION_FEE
        )[0].included === false && (
          <InfoBox
            testId={"InfoBox-PayoutProtectionFee-OptOut"}
            message={
              <>
                To opt-out of the payout protection fee, the customer must prove
                that they have met one of the opt-out eligibility conditions.
              </>
            }
          />
        )}

        <PlanOptionComponent
          name="PlanOption-PayoutProtection"
          pricingRecordState={pricingRecordState}
          pricingRecordDispatch={pricingRecordDispatch}
          optionList={[OptionalChargebeeComponents.PAYOUT_PROTECTION_FEE]}
          noneOption={{ label: "Not included", value: "", enabled: true }}
          disabled={partCOptedOut || (foQuoting21Enabled && pricingRecordState?.payout_protection_exempt === true)}
        />

        {!partCOptedOut &&
        !(foQuoting21Enabled && pricingRecordState?.payout_protection_exempt === true) &&
        pricingRecordState?.pricing_record_line_items.filter(
          (item) => item.chargebee_component === OptionalChargebeeComponents.PAYOUT_PROTECTION_FEE
        )[0].included === false && (
          <>
            <Label text="Opt-out evidence" styleCfg={{ bold: true }} />
            <div style={{ margin: "1vh 0vw" }}></div>
            <Label text={"Evidence Type"} requiredFieldInd={true} />
            <div style={{ margin: "1vh 0 2.5vh 0" }}>
              <DropDownMenu
                menuName="payout_waiver_opt_out_evidence"
                required={true}
                choices={optOutEvidenceOptions}
                onSelect={handleOptOutEvidenceChange}
                testId="PayoutWaiverOptOutEvidence-Dropdown"
                defaultVal={
                  pricingRecordState?.payout_waiver_opt_out_evidence
                    ? {
                        value:
                          pricingRecordState?.payout_waiver_opt_out_evidence,
                      }
                    : undefined
                }
              />
            </div>
            <Checkbox
              label={optOutDeclarationLabel(userDetails?.name, "payout protection")}
              onChange={() =>
                handleCheckboxChange(
                  "payout_waiver_opt_out_evidence_checked_by",
                  pricingRecordState?.payout_waiver_opt_out_evidence_checked_by
                )
              }
              id="confirmed"
              name="payout_waiver_opt_out_evidence_checked_by"
              required={true}
              checked={
                pricingRecordState?.payout_waiver_opt_out_evidence_checked_by
                  ? true
                  : false
              }
              testId="PayoutWaiverOptOutEvidence-Checkbox"
            />
          </>
        )}

        {  foQuoting21Enabled && (pricingRecordState?.servicing_and_consumables_opt_out_applicable
           || pricingRecordState?.servicing_and_consumables_exempt) && (
          <>
            <Divider />
            <div style={{ margin: "1vh 0vw" }}>
              <p>
                Please indicate if the quote will include the {" "}
                <strong>Servicing & Consumables Payment</strong>:
              </p>
            </div>
            <InfoBox
              testId={"InfoBox-ServicingAndConsumables"}
              message={
                <>
                  The Servicing & Consumables Payment covers the all vehicle servicing up until 180,000KMs &
                  up to 10 replacement tyres, 3 replacement brake sets and 4 replacement or machining
                  of brake discs.
                </>
              }
            />
            <PlanOptionComponent
              name="PlanOption-ServicingAndConsumables"
              pricingRecordState={pricingRecordState}
              pricingRecordDispatch={pricingRecordDispatch}
              optionList={[
                OptionalChargebeeComponents.SERVICING,
                OptionalChargebeeComponents.CONSUMABLES,
              ]}
              noneOption={{ label: "Not included", value: "", enabled: true }}
              includeAllOption={{ label: "Included" }}
              disabled={pricingRecordState?.servicing_and_consumables_exempt === true}
            />
          </>
        )}
        <div style={{ margin: "2.5vh 0" }}>
          <SubmitBtn text="Next" />
        </div>
      </form>
    </>
  );
}

const Divider = () => (
  <hr
    style={{ margin: "2vh 0", border: "none", borderTop: "1px solid #e0e0e0" }}
  />
);
